import { usePage, useWebsiteSettings } from 'client/store'
import { getPageId } from 'client/store/page/pageSelectors'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import { RootEntityInterface } from 'src/shared/schemas/base-entity.schema'
import { PageContentEntities } from 'src/shared/types/page-interface'
import { findEntityByType } from 'src/shared/utils/find-entity-by-type'
import { getAllDescendants } from 'src/shared/utils/get-all-descendants'
import EntityTypeEnum from '../../../common/enums/entityTypeEnum'
import { PopupInterface } from '../../../common/types/entities/popup-interface'
import { FeatureFlagsEnum } from '../shared/growthbook/feature-flags-enum'
import { useFeatureFlag } from '../shared/growthbook/use-feature-flag'
import { getWebsiteLayoutPageId } from '../store/website-settings/website-settings-selector'
import { useWebsiteSettingsForSave } from './use-website-settings-for-save'

export const useWebsitePageSavePayload = () => {
  const layoutPageId = useWebsiteSettings(getWebsiteLayoutPageId)
  const isWebsiteNewSaveLogic = useFeatureFlag(
    FeatureFlagsEnum.WebsiteNewSaveLogic,
  )
  const websiteSettings = useWebsiteSettingsForSave()
  const pageContent = usePage(page => page)
  const contentPageId = usePage(getPageId)
  const pageContentEntities = pageContent.entities as PageContentEntities
  const websiteLayoutRoot = findEntityByType(
    pageContentEntities,
    EntityTypeEnum.WebsiteLayoutBody,
  ) as RootEntityInterface | undefined
  const websiteContentRoot = findEntityByType(
    pageContentEntities,
    EntityTypeEnum.WebsitePageBody,
  )

  if (!layoutPageId || !websiteContentRoot) {
    return
  }

  const contentPageEntities = getAllDescendants(
    pageContentEntities,
    websiteContentRoot,
  )

  if (pageContent?.type === PageTypeEnum.WebsiteLogin) {
    return {
      layoutPage: {
        id: layoutPageId,
        content: {
          entities: {},
        },
      },
      contentPage: {
        id: contentPageId,
        content: {
          entities: {
            ...contentPageEntities,
            [websiteContentRoot.id]: websiteContentRoot,
          },
        },
      },
      ...(isWebsiteNewSaveLogic ? { websiteSettings } : {}),
    }
  }

  if (!websiteLayoutRoot) {
    return
  }

  const websiteLayoutRootWithoutContentRoot: RootEntityInterface = {
    ...websiteLayoutRoot,
    childIds: websiteLayoutRoot.childIds.filter(
      id => id !== websiteContentRoot?.id,
    ),
  }
  const websiteLayoutEntities = getAllDescendants(
    pageContentEntities,
    websiteLayoutRootWithoutContentRoot,
  )

  const allPopups = Object.values(pageContentEntities).filter(
    entity => entity.type === EntityTypeEnum.Popup,
  ) as PopupInterface[]

  const websiteGlobalPopups: PageContentEntities = {}
  const pagePopups: PageContentEntities = {}

  for (const popup of allPopups) {
    if (popup.isWebsiteGlobal) {
      websiteGlobalPopups[popup.id] = popup
      const popupDescendants = getAllDescendants(pageContentEntities, popup)
      Object.assign(websiteGlobalPopups, popupDescendants)
    } else {
      pagePopups[popup.id] = popup
      const popupDescendants = getAllDescendants(pageContentEntities, popup)
      Object.assign(pagePopups, popupDescendants)
    }
  }

  return {
    layoutPage: {
      id: layoutPageId,
      content: {
        entities: {
          ...websiteLayoutEntities,
          [websiteLayoutRoot.id]: websiteLayoutRootWithoutContentRoot,
          ...websiteGlobalPopups,
        },
      },
    },
    contentPage: {
      id: contentPageId,
      content: {
        entities: {
          ...contentPageEntities,
          [websiteContentRoot.id]: websiteContentRoot,
          ...pagePopups,
        },
      },
    },
    ...(isWebsiteNewSaveLogic ? { websiteSettings } : {}),
  }
}
